// Shared MetaInfo hint provider (autocomplete/VQE Phase 1).
//
// Synchronous, lazily-warmed renderer-side index over `DatabaseMetaInfo`
// so resolvers never do IPC per keystroke. Mirrors the per-session STORE
// pattern in `src/features/sql-resolver/json/pathHints/pathHintsCache.ts`.
//
// Keyed by `(connectionId, database)` and, for sub-maps, by
// `(schema, table, column)` — all folded lowercase for cross-engine
// consistency (Oracle's uppercase identifiers fold the same way).
//
// Lookups are pure/synchronous: on a cold or stale miss they return
// `undefined` (or `[]` for loose rels) and schedule a fire-and-forget
// `_warm`. `_warm` calls `fetchMetaInfo` once; an in-flight guard dedups
// concurrent warms. 60s soft-TTL.

import { fetchMetaInfo } from '@/features/workspace/common/services/metaInfoApi';
import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';
import type { LooseRelationship } from '@/types/electron';

type EnumHint = { values: readonly string[]; truncated: boolean };

type Index = {
  at: number;
  jsonPaths: Map<string, readonly string[]>;
  enumValues: Map<string, EnumHint>;
  /** Folded `(schema,table,column)` keys the user declared as JSON via
   *  MetaInfo `jsonColumns` ("Treat as JSON" — no native type, no CHECK).
   *  JCS-2 precedence: native > CHECK > this declaration. */
  declaredJson: Set<string>;
  looseRels: readonly LooseRelationship[];
};

const STORE = new Map<string, Index>();
const INFLIGHT = new Map<string, Promise<void>>();
// 60 s soft-TTL. Defense-in-depth: catches out-of-band writes that bypass the
// committed store event — for example, manual edits to the JSON file.
// UI, Blueprint import and MCP writes all emit that event. The primary invalidation
// path is the IPC bridge in `metaInfoHintWrittenBridge.ts`.
const TTL_MS = 60_000;

const dbKey = (c: string, d: string) => `${c}|${d}`.toLowerCase();
// Used for jsonPaths and declaredJson — neither has a path dimension.
// For enum-values keying, use `colPathKey` instead.
const colKey = (s: string, t: string, c: string) =>
  `${s}|${t}|${c}`.toLowerCase();
/**
 * Like `colKey` but includes the JSONPath dimension so enum caches for
 * different paths on the same column don't collide.
 * Empty string (the default) = column-level enum declaration (no path).
 */
const colPathKey = (s: string, t: string, c: string, p: string) =>
  `${s}|${t}|${c}|${p}`.toLowerCase();

function fresh(idx: Index | undefined): idx is Index {
  return !!idx && Date.now() - idx.at < TTL_MS;
}

/**
 * Stale-while-revalidate read. Returns the index whenever one is CACHED —
 * refreshing behind the caller when the soft TTL has lapsed — and `undefined`
 * only when nothing is cached at all.
 *
 * Why not "stale ⇒ nothing": the TTL is defense-in-depth against out-of-band
 * writes, not a correctness boundary (a real change calls `invalidate`, which
 * DELETES the entry, so this still returns `undefined` for it). Treating a
 * lapsed TTL as a miss made every hint the provider serves — enum values, JSON
 * paths, loose FKs — vanish from the widget for as long as the re-warm takes,
 * which is one full IPC round-trip and can cover several keystrokes. A caret
 * that happens to land in that window got NO enum values while its neighbour a
 * second earlier got all of them; serving the seconds-old copy is both correct
 * enough and stable.
 */
function cachedIndex(c: string, d: string): Index | undefined {
  const idx = STORE.get(dbKey(c, d));
  if (!idx) {
    scheduleWarm(c, d);
    return undefined;
  }
  if (!fresh(idx)) scheduleWarm(c, d);
  return idx;
}

/**
 * SQLite legacy-export fold ('' → 'main').
 *
 * Pre-2026-05-31 sqlite MetaInfo (old exports, teammate backups) keyed
 * entries with `schema: ''`; since d0fc8a18d/a73f13760 the sqlite resolver
 * looks entries up under the `'main'` convention (its schema-bus snapshot
 * carries no schema names, and every production writer uses `'main'`).
 * Rather than rewriting user data at the import boundary — the MetaInfo
 * store is engine-blind and `''` is a LEGITIMATE schema key for Kusto —
 * alias every `''`-schema index key under `'main'` at index-build time.
 * Non-clobbering: a genuine `'main'`-keyed entry always wins over the
 * alias, and the original `''` key stays (Kusto keeps resolving).
 */
function addMainSchemaAliases<V>(map: Map<string, V>): void {
  for (const [key, value] of [...map]) {
    if (!key.startsWith('|')) continue; // first segment (schema) not empty
    const alias = `main${key}`;
    if (!map.has(alias)) map.set(alias, value);
  }
}

/** Set flavour of {@link addMainSchemaAliases} (for `declaredJson`). */
function addMainSchemaAliasesToSet(set: Set<string>): void {
  for (const key of [...set]) {
    if (!key.startsWith('|')) continue;
    set.add(`main${key}`);
  }
}

function scheduleWarm(c: string, d: string): void {
  if (INFLIGHT.has(dbKey(c, d))) return;
  void _warm(c, d);
}

export async function _warm(
  connectionId: string,
  database: string,
): Promise<void> {
  const k = dbKey(connectionId, database);
  const existing = INFLIGHT.get(k);
  if (existing) return existing;
  const p = Promise.resolve().then(async () => {
    try {
      const meta = await fetchMetaInfo(connectionId, database);
      if (INFLIGHT.get(k) !== p) return;
      const jsonPaths = new Map<string, readonly string[]>();
      for (const e of meta?.jsonStructureCache ?? []) {
        jsonPaths.set(
          colKey(e.schema, e.table, e.column),
          [...e.paths]
            .sort(
              (a, b) =>
                b.prevalence - a.prevalence || a.path.localeCompare(b.path),
            )
            .map((path) => path.path),
        );
      }
      const enumValues = new Map<string, EnumHint>();
      for (const e of meta?.enumValueCache ?? []) {
        // Normalize the path on read so pre-fix on-disk entries stored with a
        // non-canonical form (e.g. 'status' instead of '$.status') are still
        // found when callers look up with the canonical path.
        // Guard: empty/undefined path = column-level entry (no JSONPath) — must
        // remain '' because normalizeJsonPath('') returns '$', not ''.
        const rawPath = e.path ?? '';
        const normalizedPath = rawPath === '' ? '' : normalizeJsonPath(rawPath);
        enumValues.set(colPathKey(e.schema, e.table, e.column, normalizedPath), {
          values: e.values,
          truncated: e.truncated,
        });
      }
      const declaredJson = new Set<string>();
      for (const d of meta?.jsonColumns ?? []) {
        declaredJson.add(colKey(d.schema, d.table, d.column));
      }
      // Legacy sqlite '' → 'main' fold — after all genuine entries are
      // indexed so real 'main' keys always win over aliases.
      addMainSchemaAliases(jsonPaths);
      addMainSchemaAliases(enumValues);
      addMainSchemaAliasesToSet(declaredJson);
      STORE.set(k, {
        at: Date.now(),
        jsonPaths,
        enumValues,
        declaredJson,
        looseRels: meta?.looseRelationships ?? [],
      });
    } catch {
      /* swallow — consumers fall back to live behaviour */
    } finally {
      if (INFLIGHT.get(k) === p) INFLIGHT.delete(k);
    }
  });
  INFLIGHT.set(k, p);
  return p;
}

export function invalidate(connectionId: string, database: string): void {
  const key = dbKey(connectionId, database);
  STORE.delete(key);
  INFLIGHT.delete(key);
}

export function getJsonPathHints(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
): readonly string[] | undefined {
  const idx = cachedIndex(connectionId, database);
  if (!idx) return undefined;
  return idx.jsonPaths.get(colKey(schema, table, column));
}

/**
 * Returns the cached enum hint for the given column (and optional path).
 *
 * Empty string or omitted `path` = column-level enum declaration (no JSONPath);
 * pass a canonical path (e.g. `'$.status'`) for JSON-path enum cache lookup.
 */
export function getEnumValues(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
  path = '',
): EnumHint | undefined {
  const idx = cachedIndex(connectionId, database);
  if (!idx) return undefined;
  return idx.enumValues.get(colPathKey(schema, table, column, path));
}

/**
 * True when the user declared `(schema,table,column)` as a JSON column via
 * MetaInfo ("Treat as JSON"). Used by the resolvers' JSON-path guard so a
 * declared-only string column (no engine-native JSON type, no CHECK) still
 * offers JSON-path autocomplete. A cold miss returns `false` and schedules a
 * warm (a STALE index is still served — see `cachedIndex`) — same eventual-consistency model as the other lookups
 * (the Table Designer toggle calls `invalidate`, so the next keystroke after
 * a re-warm reflects the change without a reconnect).
 */
export function isJsonColumnDeclared(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
): boolean {
  const idx = cachedIndex(connectionId, database);
  if (!idx) return false;
  return idx.declaredJson.has(colKey(schema, table, column));
}

export function getLooseRelationships(
  connectionId: string,
  database: string,
): readonly LooseRelationship[] {
  const idx = cachedIndex(connectionId, database);
  if (!idx) return [];
  return idx.looseRels;
}

// Test seam — flush the entire cache + in-flight guards between runs.
export function _resetMetaInfoHintsForTests(): void {
  STORE.clear();
  INFLIGHT.clear();
}
